import { useEffect, useRef, useState } from 'react';
import {
  dataWindowSecondsToValue,
  dataWindowUnitToSeconds,
  formatDataWindowValue,
  getAlarmRuleErrorMessage,
  selectionToDataWindowSeconds,
} from '../filterParameterMapping';
import { getAlarmRuleDataWindowError, getAlarmRuleDataWindowSeconds, getAlarmRuleFieldErrors } from './alarm-rule-validation';

export { getAlarmRuleFormError } from './alarm-rule-validation';

export const DATA_WINDOW_UNITS = [
  { value: 'day', label: 'Day' },
  { value: 'hours', label: 'Hours' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'seconds', label: 'Seconds' },
];

export const useAlarmRuleWindow = (initialSelection, defaultHours = 72, preserveSeconds = false) => {
  const [selection, setSelection] = useState(initialSelection);
  const seconds = getAlarmRuleDataWindowSeconds(selection);
  const error = getAlarmRuleDataWindowError(seconds);
  const relativeWindow = next => {
    const duration = getAlarmRuleDataWindowSeconds(next);
    if (getAlarmRuleDataWindowError(duration)) return next;
    const endDate = next.endDate || new Date();
    return { ...next, startDate: new Date(endDate.getTime() - duration * 1000), endDate };
  };

  const changeMode = mode => {
    setSelection(previous => {
      if (mode === 'date') return { ...previous, dataWindowMode: mode, dataWindowSeconds: null };
      const duration = selectionToDataWindowSeconds(previous, defaultHours);
      const range = previous.startDate && previous.endDate
        ? (previous.endDate.getTime() - previous.startDate.getTime()) / 1000 : defaultHours * 3600;
      return relativeWindow({
        ...previous,
        dataWindowMode: mode,
        dataWindowValue: preserveSeconds
          ? formatDataWindowValue(dataWindowSecondsToValue(duration, mode))
          : Math.max(1, Math.round(range / dataWindowUnitToSeconds[mode])),
        dataWindowSeconds: preserveSeconds ? duration : null,
      });
    });
  };

  const changeValue = value => {
    setSelection(previous => relativeWindow({
      ...previous,
      dataWindowValue: value,
      dataWindowSeconds: preserveSeconds ? Number(value) * dataWindowUnitToSeconds[previous.dataWindowMode] : null,
    }));
  };

  const reset = () => {
    const endDate = new Date();
    endDate.setSeconds(0, 0);
    setSelection(previous => ({
      ...previous,
      startDate: new Date(endDate.getTime() - defaultHours * 3600000),
      endDate,
      ...(!preserveSeconds ? { dataWindowMode: 'date', dataWindowValue: null, dataWindowSeconds: null } : {}),
    }));
  };

  return { selection, setSelection, seconds, error, changeMode, changeValue, reset };
};

export const useAlarmRuleForm = ({ initialForm, initialSubscriberIds = [], alarmRuleService, accountService, alarmNotificationService }) => {
  const [form, setForm] = useState(initialForm);
  const [touched, setTouched] = useState({});
  const [subscriberIds, setSubscriberIds] = useState(() => initialSubscriberIds.map(Number));
  const [users, setUsers] = useState([]);
  const [ruleTypes, setRuleTypes] = useState([]);
  const [ruleTypesLoading, setRuleTypesLoading] = useState(true);
  const [ruleTypesError, setRuleTypesError] = useState(null);
  const [typesVersion, setTypesVersion] = useState(0);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRuleTypesLoading(true);
    setRuleTypesError(null);
    const load = async () => {
      try {
        const types = await alarmRuleService.listRuleTypes();
        if (!cancelled && mountedRef.current) setRuleTypes(types);
      } catch (error) {
        if (cancelled || !mountedRef.current) return;
        const message = getAlarmRuleErrorMessage(error, 'Rule types could not be loaded. Please try again.');
        setRuleTypes([]);
        setRuleTypesError(message);
        alarmNotificationService.error('Failed to load rule types', message);
      } finally {
        if (!cancelled && mountedRef.current) setRuleTypesLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [alarmRuleService, alarmNotificationService, typesVersion]);

  useEffect(() => {
    let cancelled = false;
    accountService.getAll().then(result => {
      if (!cancelled && mountedRef.current) setUsers(result || []);
    }).catch(error => {
      if (!cancelled && mountedRef.current) {
        alarmNotificationService.error('Failed to load subscribers', getAlarmRuleErrorMessage(error, 'Please try again.'));
      }
    });
    return () => { cancelled = true; };
  }, [accountService, alarmNotificationService]);

  const getRuleTypeError = (includeRequired = true) => {
    if (ruleTypesLoading) return '';
    if (ruleTypesError) return ruleTypesError;
    if (!ruleTypes.length) return 'No rule types available.';
    if (form.ruleTypeId == null || form.ruleTypeId === '') return includeRequired ? 'Type is required.' : '';
    return ruleTypes.some(type => type.id === form.ruleTypeId) ? '' : 'Select an available Type.';
  };

  const changeField = (field, value) => setForm(previous => ({ ...previous, [field]: value }));
  const touchField = field => setTouched(previous => ({ ...previous, [field]: true }));
  const fieldErrors = getAlarmRuleFieldErrors(form);
  const getFieldError = field => {
    const value = form[field];
    return touched[field] || (value != null && String(value).trim() !== '') ? fieldErrors[field] || '' : '';
  };
  const toggleSubscriber = userId => {
    const id = Number(userId);
    setSubscriberIds(ids => ids.includes(id) ? ids.filter(existingId => existingId !== id) : [...ids, id]);
  };

  return {
    form,
    changeField,
    touchField,
    touched,
    getFieldError,
    subscriberIds,
    toggleSubscriber,
    users,
    ruleTypes,
    ruleTypesLoading,
    ruleTypesError,
    getRuleTypeError,
    retryRuleTypes: () => setTypesVersion(version => version + 1),
    mountedRef,
  };
};